import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, Send, LogOut, Headset, CircleDot, BellRing } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_TITLE = "Painel de Atendimento — Strondex Tech";
const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const playDing = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [880, 1174.66].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = "sine";
            const t = ctx.currentTime + i * 0.16;
            gain.gain.setValueAtTime(0.0001, t);
            gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
            osc.start(t);
            osc.stop(t + 0.5);
        });
    } catch {}
};

export default function OwnerChat() {
    const [token, setToken] = useState(() => localStorage.getItem("stx_chat_token") || "");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [totalUnread, setTotalUnread] = useState(0);
    const bottomRef = useRef(null);
    const prevUnread = useRef(-1);
    const flashRef = useRef(null);

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const startFlash = () => {
        if (flashRef.current) return;
        let on = false;
        flashRef.current = setInterval(() => {
            document.title = on ? BASE_TITLE : "Nova mensagem no chat!";
            on = !on;
        }, 900);
    };
    const stopFlash = () => {
        if (flashRef.current) clearInterval(flashRef.current);
        flashRef.current = null;
        document.title = BASE_TITLE;
    };

    useEffect(() => {
        const onFocus = () => stopFlash();
        window.addEventListener("focus", onFocus);
        return () => {
            window.removeEventListener("focus", onFocus);
            stopFlash();
        };
    }, []);

    const login = async () => {
        setError("");
        try {
            const r = await axios.post(`${API}/chat/admin/login`, { password });
            localStorage.setItem("stx_chat_token", r.data.token);
            setToken(r.data.token);
        } catch (e) {
            const d = e.response?.data?.detail;
            setError(typeof d === "string" ? d : "Erro ao entrar");
        }
    };

    const logout = () => {
        localStorage.removeItem("stx_chat_token");
        stopFlash();
        setToken("");
        setConvs([]);
        setActive(null);
    };

    useEffect(() => {
        if (!token) return;
        const ping = () => axios.post(`${API}/chat/admin/presence`, {}, auth).catch((e) => {
            if (e.response?.status === 401) logout();
        });
        ping();
        const t = setInterval(ping, 20000);
        return () => clearInterval(t);
    }, [token]);

    useEffect(() => {
        if (!token) return;
        const fetchConvs = async () => {
            try {
                const r = await axios.get(`${API}/chat/admin/conversations`, auth);
                setConvs(r.data);
                const total = r.data.reduce((s, c) => s + (c.unread_owner || 0), 0);
                if (prevUnread.current >= 0 && total > prevUnread.current) {
                    playDing();
                    startFlash();
                }
                if (total === 0) stopFlash();
                prevUnread.current = total;
                setTotalUnread(total);
            } catch (e) {
                if (e.response?.status === 401) logout();
            }
        };
        fetchConvs();
        const t = setInterval(fetchConvs, 5000);
        return () => clearInterval(t);
    }, [token]);

    useEffect(() => {
        if (!token || !active) return;
        const fetchMsgs = async () => {
            try {
                const r = await axios.get(`${API}/chat/admin/conversations/${active}/messages`, auth);
                setMessages(r.data);
            } catch {}
        };
        fetchMsgs();
        const t = setInterval(fetchMsgs, 4000);
        return () => clearInterval(t);
    }, [token, active]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const sendReply = async () => {
        const value = reply.trim();
        if (!value || !active) return;
        setReply("");
        try {
            await axios.post(`${API}/chat/admin/conversations/${active}/reply`, { text: value }, auth);
            const r = await axios.get(`${API}/chat/admin/conversations/${active}/messages`, auth);
            setMessages(r.data);
        } catch {}
    };

    if (!token) {
        return (
            <div data-testid="owner-login" className="flex min-h-screen items-center justify-center bg-[#07090E] px-5 font-sans">
                <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0D121F] p-8">
                    <div className="flex justify-center"><LogoMark size={44} /></div>
                    <h1 className="mt-5 text-center text-xl font-extrabold text-slate-50">Painel de Atendimento</h1>
                    <p className="mt-2 text-center text-sm text-slate-500">
                        Área exclusiva da Strondex Tech para responder o chat do site.
                    </p>
                    <input
                        data-testid="owner-login-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && login()}
                        placeholder="Senha de acesso"
                        className="mt-6 w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-cyan-400/60"
                    />
                    {error && <p data-testid="owner-login-error" className="mt-2 text-center text-xs font-semibold text-red-400">{error}</p>}
                    <button
                        data-testid="owner-login-button"
                        onClick={login}
                        className="mt-4 w-full rounded-full bg-cyan-500 py-3 text-sm font-bold text-[#03202e] transition-colors hover:bg-cyan-400"
                    >
                        Entrar no painel
                    </button>
                    <a href="/" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-slate-500 transition-colors hover:text-cyan-300">
                        <ArrowLeft size={13} /> Voltar ao site
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="owner-panel" className="flex h-screen flex-col bg-[#07090E] font-sans text-slate-100">
            <header className={`flex items-center justify-between border-b px-5 py-3.5 transition-colors duration-500 ${totalUnread > 0 ? "border-red-500/40 bg-[#1a0d12]" : "border-white/[0.07] bg-[#0D121F]"}`}>
                <div className="flex items-center gap-3">
                    <LogoMark size={32} />
                    <div>
                        <p className="text-sm font-extrabold">Painel de Atendimento</p>
                        <p data-testid="owner-online-badge" className="flex items-center gap-1.5 text-[11px] text-[#25D366]">
                            <CircleDot size={10} className="fill-[#25D366]" />
                            Você aparece como online no site
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {totalUnread > 0 && (
                        <span data-testid="owner-unread-alert" className="flex animate-pulse items-center gap-1.5 rounded-full bg-[#EA1D2C] px-3.5 py-1.5 text-[11px] font-extrabold text-white">
                            <BellRing size={13} />
                            {totalUnread} nova{totalUnread > 1 ? "s" : ""}
                        </span>
                    )}
                    <button data-testid="owner-logout" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-bold text-slate-400 transition-colors hover:border-red-400/40 hover:text-red-300">
                        <LogOut size={13} /> Sair
                    </button>
                </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
                <aside data-testid="owner-inbox" className={`w-full flex-col overflow-y-auto border-r border-white/[0.07] bg-[#0D121F]/60 sm:flex sm:w-80 ${active ? "hidden" : "flex"}`}>
                    <p className="border-b border-white/[0.05] px-5 py-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                        Conversas ({convs.length})
                    </p>
                    {convs.length === 0 && (
                        <p className="px-5 py-10 text-center text-sm text-slate-600">
                            Nenhuma conversa ainda. Quando um visitante chamar no chat do site, aparece aqui.
                        </p>
                    )}
                    {convs.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`owner-conv-${c.id}`}
                            onClick={() => setActive(c.id)}
                            className={`border-b border-white/[0.05] px-5 py-4 text-left transition-colors hover:bg-white/[0.04] ${
                                active === c.id ? "bg-cyan-400/[0.07]" : ""
                            } ${c.unread_owner > 0 ? "bg-[#25D366]/[0.05]" : ""}`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-sm font-bold text-slate-100">{c.visitor}</span>
                                {c.unread_owner > 0 && (
                                    <span className="flex h-5 min-w-5 animate-pulse items-center justify-center rounded-full bg-[#25D366] px-1.5 text-[10px] font-extrabold text-[#052e12]">
                                        {c.unread_owner}
                                    </span>
                                )}
                            </div>
                            <p className="mt-1 truncate text-xs text-slate-500">{c.last_text || "Conversa iniciada"}</p>
                        </button>
                    ))}
                </aside>

                <main className={`flex-1 flex-col sm:flex ${active ? "flex" : "hidden"}`}>
                    {active ? (
                        <>
                            <div className="flex items-center gap-3 border-b border-white/[0.07] px-5 py-3.5">
                                <button onClick={() => setActive(null)} className="rounded-lg border border-white/10 p-1.5 text-slate-400 sm:hidden" aria-label="Voltar">
                                    <ArrowLeft size={16} />
                                </button>
                                <Headset size={17} className="text-cyan-300" />
                                <p className="text-sm font-bold">{convs.find((c) => c.id === active)?.visitor}</p>
                            </div>
                            <div data-testid="owner-messages" className="flex-1 space-y-2.5 overflow-y-auto p-5">
                                {messages.map((m) => (
                                    <div key={m.id} className={`flex ${m.sender === "owner" ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                                            m.sender === "owner"
                                                ? "rounded-br-md bg-cyan-500 text-[#03202e]"
                                                : "rounded-bl-md border border-white/[0.07] bg-white/[0.06] text-slate-200"
                                        }`}>
                                            <p>{m.text}</p>
                                            <p className={`mt-1 text-right text-[10px] ${m.sender === "owner" ? "text-[#03202e]/60" : "text-slate-600"}`}>
                                                {fmtTime(m.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                <div ref={bottomRef} />
                            </div>
                            <div className="flex items-center gap-2 border-t border-white/[0.07] p-4">
                                <input
                                    data-testid="owner-reply-input"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && sendReply()}
                                    placeholder="Responder..."
                                    className="flex-1 rounded-full border border-white/10 bg-white/[0.05] px-4 py-3 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-cyan-400/60"
                                />
                                <button
                                    data-testid="owner-reply-send"
                                    onClick={sendReply}
                                    disabled={!reply.trim()}
                                    aria-label="Enviar resposta"
                                    className="rounded-full bg-cyan-500 p-3 text-[#03202e] transition-colors hover:bg-cyan-400 disabled:opacity-40"
                                >
                                    <Send size={17} />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="hidden flex-1 items-center justify-center text-sm text-slate-600 sm:flex">
                            Selecione uma conversa para responder
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
